import { useState, useEffect, useReducer } from "react";
import { gql } from "apollo-boost";
import { useLazyQuery } from "@apollo/react-hooks";
import { useRequest, stateSwitch } from "../interfaces/request";
import ApolloClient from "../services/apollo";

import { Ticket } from "../interfaces/ticket";

export const GET_TICKET = gql`
  query getTicket($id: String!) {
    getTicket(id: $id) {
      price
      status
      date
      hour
      minutes
      created_at
      code
      observation
      car {
        number
        organization {
          name
          image
        }
      }
      arrivalLocation {
        name
      }
      departureLocation {
        name
      }
    }
  }
`;


export const MUTATION_STATUS = gql`
  mutation setUpdateStatus($id: String!, $status: TicketStatusEnum!) {
    updateTicketStatus(id: $id, status: $status) {
      status
    }
  }
`;


interface useTicketVariables {
  id: any;
}

export const useTicket = (
  variables: useTicketVariables,
  queryOptions: any = {}
) => {
  const [ticket, setTicket] = useState<Ticket>({
    id: 0,
    date: "",
    hour: 0,
    minutes: 0,
    status: 1,
    arrivalLocation: { name: "" },
    departureLocation: { name: "" },
    code: "",
    observation: "",
    car: {
      board: "",
      number: "",
      organization: {
        name: "",
      },
    },
    price: "",
    created_at: "",
  });

  const [getTicket, { loading, data, error, stopPolling }] = useLazyQuery(
    GET_TICKET,
    {
      variables,
      ...queryOptions,
    }
  );

  useEffect(() => {
    getTicket();
  }, []);

  useEffect(() => {
    if (data && data.getTicket) {
      setTicket((prevTicket) => ({
        ...prevTicket,
        ...data.getTicket,
      }));
    }
  }, [data]);

  return {
    ticket,
    data,
    loading,
    error,
    stopPolling,
  };
};

interface useButtonsState extends useRequest {
  showAlertConfirm: boolean;
  showAlertCancel: boolean;
  confirm: boolean;
  cancel: boolean;
};

interface ActionState {
  type: "loading" | "success" | "error" | "showAlertConfirm" | "showAlertCancel" | "confirm" | "cancel";
  value?: any;
}

const initialButtonsState: useButtonsState = {
  showAlertConfirm: false,
  showAlertCancel: false,
  loading: false,
  error: false,
  success: false,
  confirm: false,
  cancel: false
};

const reducerButtonsState = (state: useButtonsState, action: ActionState) => {
  switch(action.type) {
    case "confirm":
      return {
        ...state,
        confirm: action.value
      };
    case "cancel":
      return {
        ...state,
        cancel: action.value
      };
    case "showAlertConfirm":
      return {
        ...state,
        showAlertConfirm: action.value
      };
    case "showAlertCancel":
      return {
        ...state,
        showAlertCancel: action.value
      };
    default:
      return stateSwitch(state, action);
  }
};

export const useButtonsState = (id?: string | number) => {
  const [reducer, dispatch] = useReducer(reducerButtonsState, initialButtonsState);
  const send = (type: "confirm" | "cancel" = "confirm") => {
    dispatch({type: "loading", value: true});
    dispatch({type, value: true});
    dispatch({type: type === "confirm" ? "cancel" : "confirm", value: false});
    ApolloClient.mutate({
      mutation: MUTATION_STATUS,
      variables: {
        id,
        status: type=== "confirm" ? "CONFIRM" : "CANCEL"
      }
    }).then(res => {
      dispatch({type: "loading", value: false});
      dispatch({type: "success", value: true});
      dispatch({type: "showAlertCancel", value: false});
      dispatch({type: "showAlertConfirm", value: false});
    }).catch(err => {
      dispatch({type: "loading", value: false});
      dispatch({type: "success", value: false});
      dispatch({type: "error", value: true});

      dispatch({type: "showAlertCancel", value: false});
      dispatch({type: "showAlertConfirm", value: false});

      dispatch({type, value: false});
    })
  };
  return {
    ...reducer,
    send,
    handleAlertConfirm: (value=true) => dispatch({type: "showAlertConfirm", value}),
    handleAlertCancel: (value=true) => dispatch({type: "showAlertCancel", value}),
    closeSuccess: () => {
      dispatch({type: "success", value: false});
      dispatch({type: "loading", value: false});
    }
  };
}
